import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import { NAVBAR } from "@/constants/testIds";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "How It Works", to: "/how-it-works" },
    { label: "For Clinics", to: "/for-clinics" },
    { label: "For Partners", to: "/for-partners" },
    { label: "Compliance", to: "/compliance" },
  ];

  const isActive = (to) => location.pathname === to;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100" : "bg-transparent"
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid={NAVBAR.logo} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Apexum</span>
              <span className="font-medium text-blue-600 text-sm"> Partners</span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {links.map(l => (
              <Link key={l.to} to={l.to}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(l.to) ? "text-blue-600" : "text-slate-600 hover:text-slate-900"
                }`}>
                {l.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/admin" data-testid="navbar-admin-btn"
              className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 transition-colors duration-200 text-sm font-medium px-3 py-2 rounded-full hover:bg-slate-100">
              <Lock size={13} />
              Admin
            </Link>
            <Link to="/talk-to-ava" data-testid={NAVBAR.talkToAvaBtn}
              className="bg-blue-600 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-blue-700 transition-all duration-200 shadow-md shadow-blue-600/20">
              Talk to AvA
            </Link>
          </div>

          <button data-testid={NAVBAR.menuToggle} onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-slate-600 hover:text-slate-900">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-b border-slate-100 px-6 py-4 space-y-3">
          {links.map(l => (
            <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
              className="block text-sm font-medium text-slate-700 hover:text-blue-600 py-1">
              {l.label}
            </Link>
          ))}
          <Link to="/talk-to-ava" onClick={() => setMobileOpen(false)}
            className="block bg-blue-600 text-white text-center px-5 py-2.5 rounded-full text-sm font-medium mt-2">
            Talk to AvA
          </Link>
          <Link to="/admin" onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center gap-1.5 text-slate-400 text-sm font-medium py-2 mt-1">
            <Lock size={13} /> Admin Login
          </Link>
        </div>
      )}
    </nav>
  );
}
